import { cn } from '@neo/lib/utils'
import { Lock } from 'lucide-react'
import { ProductTag } from 'v2/shared/onboarding/product-tag'
import { Radio } from './radio'
import type { BuildChoice, BuildOption, Framework, FrameworkOption } from './wizard-data'

// The card border lives in StepBuild, so a follow-up panel can share the block.
export const BuildCard = ({
  option,
  selected,
  expanded,
  onSelect,
}: {
  option: BuildOption
  selected: boolean
  // A follow-up panel renders directly below, so the bottom padding shrinks.
  expanded: boolean
  onSelect: (value: BuildChoice) => void
}) => {
  return (
    <button
      type="button"
      onClick={() => onSelect(option.value)}
      // A toggle, not a radio: clicking the selected option clears it.
      aria-pressed={selected}
      aria-expanded={expanded}
      data-testid={`onboarding-build-${option.value}`}
      className={cn(
        'flex w-full items-start gap-3 px-4 pt-4 text-left',
        expanded ? 'pb-2' : 'pb-4',
      )}
    >
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        {/* min-h-5 keeps descriptions aligned on cards with and without a badge. */}
        <span className="flex min-h-5 flex-wrap items-center gap-2">
          <span className="text-p-600 text-foreground">{option.title}</span>
          {option.badge && <ProductTag label={option.badge.label} />}
          {option.restriction && (
            // Beside the title, in the quietest grey.
            <span
              className="flex items-center gap-1 text-s-400 text-tertiary"
              data-testid={`onboarding-restriction-${option.value}`}
            >
              <Lock className="h-3 w-3 shrink-0" strokeWidth={2} aria-hidden />
              {option.restriction}
            </span>
          )}
        </span>
        <span className="text-s-400 text-secondary">{option.description}</span>
      </span>
      <Radio selected={selected} />
    </button>
  )
}

export const FrameworkPill = ({
  option,
  selected,
  onSelect,
}: {
  option: FrameworkOption
  selected: boolean
  onSelect: (value: Framework) => void
}) => (
  <button
    type="button"
    onClick={() => onSelect(option.value)}
    // aria-pressed is the only selection signal a screen reader gets.
    aria-pressed={selected}
    data-testid={`onboarding-framework-${option.value}`}
    className={cn(
      'group flex w-fit items-center gap-2 rounded-lg border bg-background px-2.5 py-2 text-left transition-colors',
      selected ? 'border-primary' : 'cursor-pointer border-st2 hover:border-st1',
    )}
  >
    <Radio selected={selected} />
    {/* text-secondary, not tertiary: tertiary fails AA contrast for a label. */}
    <span
      className={cn(
        'whitespace-nowrap text-s-500 transition-colors',
        selected ? 'text-foreground' : 'text-secondary group-hover:text-foreground',
      )}
    >
      {option.label}
    </span>
  </button>
)
